import { useEffect, useState } from "react";
import {
    Chip,
    CircularProgress,
    Collapse,
    Drawer,
    Stack,
    Typography,
} from "@mui/material";
import { KeyboardArrowDownRounded } from "@mui/icons-material";
import { toast } from "react-toastify";
import { listVersions } from "../api";
import { formatDateTime } from "../utils/format";
import { apiError } from "../utils/meta";

/**
 * Read-only version history for roles, pipelines and workflows — every edit
 * keeps the prior definition. Click a version to inspect its JSON. No
 * restore; this is a paper trail, not a time machine.
 */
export default function VersionHistoryDrawer({
    open,
    onClose,
    kind,
    entityId,
    title,
}) {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        if (!open || !entityId) return;
        setExpanded(null);
        (async () => {
            setLoading(true);
            try {
                const { data } = await listVersions(kind, entityId);
                setVersions(Array.isArray(data) ? data : (data?.data ?? []));
            } catch (err) {
                toast.error(apiError(err, "Failed to load version history"));
            } finally {
                setLoading(false);
            }
        })();
    }, [open, kind, entityId]);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: { xs: "100%", md: 520 }, background: "var(--bg)" },
            }}
        >
            <Stack sx={{ height: "100%" }}>
                <Stack
                    sx={{
                        p: "24px 28px 20px",
                        borderBottom: "1px solid var(--border)",
                        gap: 0.5,
                        background: "var(--bg-secondary)",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 11,
                            letterSpacing: "2px",
                            fontWeight: 700,
                            color: "var(--text-tertiary)",
                        }}
                    >
                        VERSION HISTORY
                    </Typography>
                    <Typography variant="h5">{title}</Typography>
                    <Typography
                        sx={{ fontSize: 13, color: "var(--text-secondary)" }}
                    >
                        Every save keeps the previous definition. Read-only.
                    </Typography>
                </Stack>

                <Stack
                    sx={{ flex: 1, overflowY: "auto", p: "20px 28px", gap: 1.25 }}
                >
                    {loading && (
                        <Stack sx={{ alignItems: "center", py: 6 }}>
                            <CircularProgress size={26} />
                        </Stack>
                    )}
                    {!loading && versions.length === 0 && (
                        <Typography
                            sx={{
                                fontSize: 14,
                                color: "var(--text-secondary)",
                                textAlign: "center",
                                py: 6,
                            }}
                        >
                            No prior versions yet — edits will appear here.
                        </Typography>
                    )}
                    {!loading &&
                        versions.map((v) => {
                            const isOpen = expanded === v.id;
                            return (
                                <Stack
                                    key={v.id}
                                    sx={{
                                        border: "1px solid var(--border-mid)",
                                        borderRadius: "4px",
                                        background: "var(--bg-secondary)",
                                    }}
                                >
                                    <Stack
                                        onClick={() =>
                                            setExpanded(isOpen ? null : v.id)
                                        }
                                        sx={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            p: "12px 14px",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <Stack
                                            sx={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                                gap: 1.5,
                                            }}
                                        >
                                            <Chip
                                                size="small"
                                                label={`v${v.version}`}
                                                sx={{
                                                    height: 22,
                                                    fontSize: 11.5,
                                                    fontWeight: 600,
                                                    background:
                                                        "var(--bg-tint-soft)",
                                                    border: "1px solid var(--border-mid)",
                                                    color: "var(--text-strong-muted)",
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {v.createdByName || "Unknown"}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    color: "var(--text-secondary)",
                                                }}
                                            >
                                                {formatDateTime(v.createdAt)}
                                            </Typography>
                                        </Stack>
                                        <KeyboardArrowDownRounded
                                            sx={{
                                                fontSize: 18,
                                                color: "var(--text-tertiary)",
                                                transform: isOpen
                                                    ? "rotate(180deg)"
                                                    : "none",
                                                transition: "transform 0.15s",
                                            }}
                                        />
                                    </Stack>
                                    <Collapse in={isOpen}>
                                        <Stack
                                            sx={{
                                                borderTop:
                                                    "1px solid var(--border)",
                                                p: "12px 14px",
                                            }}
                                        >
                                            <Typography
                                                component="pre"
                                                sx={{
                                                    m: 0,
                                                    fontSize: 12,
                                                    fontFamily:
                                                        "'SF Mono', Menlo, monospace",
                                                    whiteSpace: "pre-wrap",
                                                    wordBreak: "break-word",
                                                    color: "var(--text-secondary)",
                                                    maxHeight: 360,
                                                    overflowY: "auto",
                                                }}
                                            >
                                                {JSON.stringify(
                                                    v.definition,
                                                    null,
                                                    2,
                                                )}
                                            </Typography>
                                        </Stack>
                                    </Collapse>
                                </Stack>
                            );
                        })}
                </Stack>
            </Stack>
        </Drawer>
    );
}
